import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PRAY_MODE_ENABLED } from '@/lib/feature-flags';
import { formatRelativeTime } from '@/lib/prayer-utils';
import { anchoredPrayers, JOURNAL_PAGE_SIZE } from '@/lib/journal-aggregate';
import { useJournalSources } from '@/hooks/useJournalSources';
import BibleThemePicker from '@/components/BibleThemePicker/BibleThemePicker';
import { useNoticeStackReserve } from '@/hooks/useNoticeStackReserve';
import JournalPagination from '@/components/JournalPagination/JournalPagination';
import JournalSourceState from '@/components/JournalSourceState/JournalSourceState';
import { useSettingsStore } from '@/lib/settings-store';
import styles from './JournalPrayedPage.module.css';

/**
 * Scripture Journal "Prayers on Scripture" lens (canon-spatial redesign Phase 4,
 * §6). A leaf route (rule #10: own back Link, native browser back, NO NavStack
 * layer), pray-gated. Lists every scripture-anchored prayer newest-first; each
 * row (anchor reference + "Prayed" marker + clamped prayer text + relative time)
 * routes to its passage page — the canon-spatial "open my Bible to here"
 * destination shared with the other lenses and the most-recent jump-back.
 *
 * No BSB load: prayers carry their frozen anchor reference and rawText. Rows use
 * incremental paging (JOURNAL_PAGE_SIZE slice + Show more). Private prayers are
 * INCLUDED (rule #27: the journal is in-app only, no outbound surface).
 */
export default function JournalPrayedPage() {
  const { t } = useTranslation();
  const { sources, state: sourceState, ready, retry } = useJournalSources();
  const bibleTheme = useSettingsStore((s) => s.bibleTheme);
  // App notice stack (v0.2.0 finishing): an in-flow spacer at the top of the
  // page (above the Back link and the theme picker, so the picker's menu also
  // opens below the notices) keeps the page below the fixed notices at the
  // top; 0 while no notice shows. Each render path mounts its own spacer with
  // the same callback ref.
  const [noticeReserve, noticeReserveRef] = useNoticeStackReserve();

  const prayers = useMemo(
    () => anchoredPrayers(sources.prayers),
    [sources.prayers],
  );
  const [page, setPage] = useState(0);

  useEffect(() => {
    const prior = document.title;
    document.title = t('bible.journalPrayedTitle');
    return () => {
      document.title = prior;
    };
  }, [t]);

  if (!PRAY_MODE_ENABLED) return <Navigate to="/bible/bookmarks" replace />;

  // Wait for the source loads to settle before treating empty as truly empty
  // (a direct load / refresh can render before IDB hydrates; codex BLOCKER).
  if (!ready)
    return (
      <div className={styles.readerRoot} data-bible-theme={bibleTheme}>
        <div className={styles.readerBg} aria-hidden="true" />
        <div className={styles.container} data-bible-theme={bibleTheme}>
          <div
            ref={noticeReserveRef}
            className={styles.noticeReserve}
            style={{ height: noticeReserve }}
            data-notice-reserve={noticeReserve}
            aria-hidden="true"
          />
          <div className={styles.topBar}>
            <Link to="/bible/bookmarks" className={styles.backLink}>
              {t('bible.back')}
            </Link>
            <div className={styles.headerActions}>
              <BibleThemePicker />
            </div>
          </div>
          <h1 className={styles.title}>{t('bible.journalPrayedTitle')}</h1>
          <JournalSourceState state={sourceState} onRetry={retry} />
        </div>
      </div>
    );
  // No anchored prayers (the home only shows the lens when there are some; this
  // is a direct URL) -> back home.
  if (prayers.length === 0) return <Navigate to="/bible/bookmarks" replace />;

  const pageCount = Math.max(1, Math.ceil(prayers.length / JOURNAL_PAGE_SIZE));
  const boundedPage = Math.min(page, pageCount - 1);
  const pageStart = boundedPage * JOURNAL_PAGE_SIZE;
  const shown = prayers.slice(pageStart, pageStart + JOURNAL_PAGE_SIZE);

  return (
    <div className={styles.readerRoot} data-bible-theme={bibleTheme}>
      <div className={styles.readerBg} aria-hidden="true" />
      <div className={styles.container} data-bible-theme={bibleTheme}>
      <div
        ref={noticeReserveRef}
        className={styles.noticeReserve}
        style={{ height: noticeReserve }}
        data-notice-reserve={noticeReserve}
        aria-hidden="true"
      />
      <div className={styles.topBar}>
        <Link to="/bible/bookmarks" className={styles.backLink}>
          {t('bible.back')}
        </Link>
        <div className={styles.headerActions}>
          <BibleThemePicker />
        </div>
      </div>
      <h1 className={styles.title}>{t('bible.journalPrayedTitle')}</h1>

      <ul className={styles.list}>
        {shown.map((p) => {
          const a = p.scriptureAnchor!;
          const timeAgo = formatRelativeTime(new Date(p.createdAt).getTime(), t);
          return (
            <li key={p.id} className={styles.rowItem}>
              <Link
                to={`/bible/bookmarks/book/${a.bookSlug}/${a.chapter}`}
                className={styles.row}
              >
                <div className={styles.rowTop}>
                  <span className={styles.rowRef}>{a.reference}</span>
                  <span className={styles.rowTime}>{timeAgo}</span>
                </div>
                <span className={styles.prayerLabel}>
                  <span className={styles.prayerGlyph} aria-hidden="true">
                    ✦
                  </span>
                  {t('bookmark.prayerLabel')}
                </span>
                {p.rawText ? <p className={styles.rowNote}>{p.rawText}</p> : null}
              </Link>
            </li>
          );
        })}
      </ul>
      <JournalPagination
        page={boundedPage}
        totalItems={prayers.length}
        onPageChange={setPage}
      />
      </div>
    </div>
  );
}
